import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const SivellsBendCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Sivells Bend ISD: From C to A Rating in One Year | Curipod Case Study"
      subTitle="See how Curipod drives measurable student growth — from 75% to 100% Meets & Masters on STAAR at Sivells Bend ISD, a small district that leaped from a C to an A rating in one year."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>From C to A in One Year</HeroTitle>
            <HeroSubtitle>Sivells Bend&apos;s Unprecedented Gains</HeroSubtitle>
            <HeroDescription>
              How one teacher at Sivells Bend ISD in Gainesville, Texas — a district of
              just 74 students — reached 100% Meets &amp; Masters on STAAR and helped the
              district leap from a C to an A rating in a single year.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Sivells Bend ISD</MetaBadge>
              <MetaBadge>8th Grade ELAR &amp; Social Studies</MetaBadge>
              <MetaBadge>2024–2025 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
              <Link href="/quotes?step=role" passHref>
                <a>
                  <HeroCtaButton type="primary">Book a demo</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    75<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>STAAR ELAR Approaches+</StatLabel>
                  <StatYear>2023–2024 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    100<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>STAAR ELAR Approaches+</StatLabel>
                  <StatYear>2024–2025 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatNumber>
                    75<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Social Studies Meets &amp; Masters</StatLabel>
                  <StatYear>2023–2024 · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    100<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Social Studies Meets &amp; Masters</StatLabel>
                  <StatYear>2024–2025 · With Curipod</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* DOWNLOAD CTA (below hero) */}
      <DownloadCtaBanner>
        <MaxWidthContainer>
          <DownloadCtaInner>
            <DownloadCtaLabel>
              Want the full story? Download the PDF version of this case study.
            </DownloadCtaLabel>
            <a
              href="/Curipod_Case_Study_Sivells_Bend.pdf"
              download="Curipod_Case_Study_Sivells_Bend.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 44 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadCtaInner>
        </MaxWidthContainer>
      </DownloadCtaBanner>

      {/* A SMALL DISTRICT WITH BIG RESULTS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>A SMALL DISTRICT WITH BIG RESULTS</SectionTag>
              <SectionTitle>Where Every Teacher Wears Multiple Hats</SectionTitle>
              <SectionText>
                Sivells Bend ISD sits in Gainesville, Texas, with just{' '}
                <strong>74 students in grades PK–8</strong>. In a district this small,
                teachers and students know each other well — sometimes for years.
                It&apos;s a close-knit community with limited resources and no curriculum
                director, where every teacher wears multiple hats.
              </SectionText>
              <SectionText>
                For middle school ELAR and Social Studies teacher{' '}
                <strong>Eva Wolfenbarger</strong>, this environment is both rewarding and
                challenging. Mixed-age classrooms, deep personal connections, and a
                culture of accountability means feedback can be tricky.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  It was like grading my own child at times. You feel too harsh, or too
                  generous, because you know what they&apos;re trying to do.
                </QuoteText>
                <QuoteAttrib>
                  — Eva Wolfenbarger, ELAR &amp; Social Studies Teacher
                </QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <CircularImageWrap $size={150}>
                  <Image
                    src="/case-study-sivells-bend/eva-student.jpeg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    alt="Eva Wolfenbarger with a graduating student"
                  />
                </CircularImageWrap>
                <TeamPhotoCaption>
                  Eva Wolfenbarger with a graduating student
                </TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>The Sivells Bend Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>ELAR &amp; Social Studies Teacher</TeamRole>
                  <TeamName>Eva Wolfenbarger</TeamName>
                  <TeamNote>Middle school, mixed-age classrooms</TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>District</TeamRole>
                  <TeamName>Sivells Bend ISD</TeamName>
                  <TeamNote>74 students, grades PK–8</TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Location</TeamRole>
                  <TeamName>Gainesville, Texas</TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              THE CHALLENGE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>The Feedback Gap</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 20px' }}
            >
              Before finding Curipod, feedback cycles often stretched too long. Students
              might wait days between submitting work and receiving feedback — losing the
              opportunity to make immediate, meaningful improvements.
            </SectionText>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              As John Hattie&apos;s research shows, real-time feedback is one of the most
              effective ways to boost achievement. Eva wanted that immediacy without
              adding hours to her workload. She always knew what would make the biggest
              difference for her students — but{' '}
              <strong>
                finding the time and tools to do it consistently was a challenge
              </strong>
              .
            </SectionText>

            <FocusAreaGrid>
              {[
                'Support Multi-Grade Classrooms',
                'Close the Feedback Gap',
                'Student Growth Across the Board',
              ].map(area => (
                <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
              ))}
            </FocusAreaGrid>

            <BlockQuote style={{ maxWidth: 680, margin: '40px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                You write something, and I give it back to you tomorrow. Then you rewrite
                it, and I give it back to you another day later… That time between is so
                valuable.
              </QuoteText>
              <QuoteAttrib>
                — Eva Wolfenbarger, ELAR &amp; Social Studies Teacher
              </QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* THE CURIPOD SHIFT */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            THE CURIPOD SHIFT
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            How Curipod Changed Everything
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>⚡</ImpactIcon>
              <ImpactTitle>Instant, Actionable Feedback</ImpactTitle>
              <ImpactQuote>
                &ldquo;It condenses the feedback cycle so that I can spend more time doing
                those things I need to do as a teacher, and Curipod&apos;s catching what I
                can&apos;t.&rdquo;
              </ImpactQuote>
              <ImpactAttrib>— Eva Wolfenbarger</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>👁️</ImpactIcon>
              <ImpactTitle>Universal Participation</ImpactTitle>
              <ImpactQuote>
                &ldquo;With Curipod, students cannot hide.&rdquo; Instructional pacing
                stayed in Eva&apos;s control and participation was non-negotiable.
                Students wrote, reflected, and revised in one class period.
              </ImpactQuote>
              <ImpactAttrib>— Eva Wolfenbarger</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>📝</ImpactIcon>
              <ImpactTitle>Write, Reflect, Revise — In One Period</ImpactTitle>
              <ImpactQuote>
                With Curipod, feedback was instant. Students wrote, reflected, and revised
                in one class period, and Eva could focus her energy on teaching, not
                chasing papers.
              </ImpactQuote>
              <ImpactAttrib>— Eva Wolfenbarger</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Teaching Made Easier, Results Made Stronger</ImpactTitle>
              <ImpactQuote>
                With Curipod reducing the workload, Eva could focus on what matters most:
                student success. By the end of the year, Eva&apos;s results spoke for
                themselves.
              </ImpactQuote>
              <ImpactAttrib>— Eva Wolfenbarger</ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>

          <ScreenshotRow>
            <ScreenshotItem>
              <ScreenshotWrap>
                <Image
                  src="/case-study-sivells-bend/curipod-lesson.png"
                  layout="fill"
                  objectFit="contain"
                  alt="Curipod lesson: Read the Passage activity"
                />
              </ScreenshotWrap>
              <ScreenshotCaption>Curipod lesson activity</ScreenshotCaption>
            </ScreenshotItem>
            <ScreenshotItem>
              <ScreenshotWrap>
                <Image
                  src="/case-study-sivells-bend/curipod-feedback.png"
                  layout="fill"
                  objectFit="contain"
                  alt="Curipod AI feedback on student writing"
                />
              </ScreenshotWrap>
              <ScreenshotCaption>AI-powered feedback</ScreenshotCaption>
            </ScreenshotItem>
            <ScreenshotItem>
              <ScreenshotWrap>
                <Image
                  src="/case-study-sivells-bend/feedback-chart.png"
                  layout="fill"
                  objectFit="contain"
                  alt="Student reflection on feedback improvements"
                />
              </ScreenshotWrap>
              <ScreenshotCaption>Student feedback reflection</ScreenshotCaption>
            </ScreenshotItem>
          </ScreenshotRow>
        </MaxWidthContainer>
      </Section>

      {/* BREAKTHROUGH FOR A STRUGGLING STUDENT */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              BREAKTHROUGH FOR A STRUGGLING STUDENT
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              From a 2 to a 5 — and Passing STAAR
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              Eva&apos;s most powerful Curipod moment came from a student who had long
              struggled. She scored 2 out of 5 on the STAAR writing response — and one
              year, she didn&apos;t complete the writing section at all.
            </SectionText>

            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              As STAAR approached, Eva&apos;s class used Curipod to practice. The
              student&apos;s first try scored a 2. She received Curipod AI feedback,
              reflected, revised, and received another round of feedback. Eva recalls,
              &ldquo;She just stared at her screen.&rdquo; The student had earned a 5! Her
              confidence soared.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                Miss, Curipod gave me a five. I can pass the STAAR!
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Student, Sivells Bend ISD
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              Come spring, she did pass — and{' '}
              <strong>
                her parents celebrated at parent night because she finally passed STAAR
              </strong>
              .
            </ResultNote>

            <ResultsStudentsPhoto>
              <CircularImageWrap $size={160} $borderColor="rgba(249,151,70,0.6)">
                <Image
                  src="/case-study-sivells-bend/eva-student.jpeg"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  alt="Eva Wolfenbarger with a graduating student"
                />
              </CircularImageWrap>
              <ResultsPhotoLabel>
                Eva Wolfenbarger celebrating student success
              </ResultsPhotoLabel>
            </ResultsStudentsPhoto>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              When Strong Teaching Meets the Right Tools
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Eva has always known what makes the biggest difference for her students.
              With Curipod, she can deliver it every time — because the best thing to do
              is finally the easiest thing to do.
            </SectionText>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              In Sivells Bend, every student is meeting or exceeding expectations, and
              teachers are recognizing what&apos;s possible when strong teaching meets the
              right tools.
            </SectionText>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                I do not understand why every single teacher is not using this&hellip; we
                just used it half a year and look how much progress we made.
              </FinalQuoteText>
              <QuoteAttrib>
                — Eva Wolfenbarger, ELAR &amp; Social Studies Teacher
              </QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {[
                'Feedback',
                'Writing Growth',
                'Engagement',
                'Equity',
                'Small Districts',
              ].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
              <Link href="/quotes?step=role" passHref>
                <a>
                  <Button
                    type="tertiary"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Book a demo
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Sivells Bend case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Sivells_Bend.pdf"
              download="Curipod_Case_Study_Sivells_Bend.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default SivellsBendCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;

  @media ${MOBILE} {
    align-items: flex-start;
  }
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── DOWNLOAD CTA BANNER (below hero) ──────────────────────────────────────── */

const DownloadCtaBanner = styled.div`
  background: ${theme.colors.beigeDark};
  border-bottom: 1px solid rgba(249, 151, 70, 0.2);
  padding: 16px 0;
`;

const DownloadCtaInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const DownloadCtaLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.dark};

  @media ${MOBILE} {
    font-size: 14px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CIRCULAR IMAGE ─────────────────────────────────────────────────────────── */

const CircularImageWrap = styled.div<{ $size: number; $borderColor?: string }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${({ $borderColor }) => $borderColor ?? theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── SCREENSHOTS ────────────────────────────────────────────────────────────── */

const ScreenshotRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 48px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ScreenshotItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ScreenshotWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${theme.effects.defaultShadow};
  background: ${theme.colors.white};
`;

const ScreenshotCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 48px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const ResultsStudentsPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultsPhotoLabel = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
